/**
 * Public holidays (format: MM-DD for recurring holidays)
 */
const PUBLIC_HOLIDAYS = [
  { month: 4, day: 3, name: 'Easter' },
  { month: 4, day: 6, name: 'Easter Monday' },
  { month: 5, day: 1, name: "Worker's Day" },
  { month: 5, day: 27, name: 'Big EID' },
  { month: 5, day: 28, name: 'Big EID' },
  { month: 6, day: 12, name: 'Democracy Day' },
  { month: 8, day: 26, name: 'Maulud' },
  { month: 10, day: 1, name: 'Independence Day' },
  { month: 12, day: 25, name: 'Christmas' },
  { month: 12, day: 26, name: 'Boxing Day' },
  { month: 12, day: 27, name: 'Christmas Holiday' },
  { month: 12, day: 28, name: 'Christmas Holiday' },
];

/**
 * Check if a date is a public holiday
 * @param {Date} date - Date to check
 * @returns {boolean} True if public holiday, false otherwise
 */
function isPublicHoliday(date) {
  const month = date.getMonth() + 1; // getMonth() returns 0-11
  const day = date.getDate();
  
  return PUBLIC_HOLIDAYS.some(holiday => 
    holiday.month === month && holiday.day === day
  );
}

/**
 * Calculate business days between two dates (excluding weekends and public holidays)
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @param {boolean} includeStartDate - Whether to include the start date (default: true)
 * @returns {number} Number of business days
 */
export function calculateBusinessDays(
  startDate,
  endDate,
  includeStartDate = true,
) {
  if (!startDate || !endDate) return 0;

  const start = new Date(startDate);
  const end = new Date(endDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return 0;
  }

  if (start > end) {
    return 0;
  }

  let businessDays = 0;
  const current = new Date(start);

  // If we don't include start date, move to next day
  if (!includeStartDate) {
    current.setDate(current.getDate() + 1);
  }

  while (current <= end) {
    // 0 = Sunday, 6 = Saturday
    const dayOfWeek = current.getDay();
    const isWeekend = dayOfWeek === 0 || dayOfWeek === 6;
    const isHoliday = isPublicHoliday(current);
    
    if (!isWeekend && !isHoliday) {
      businessDays++;
    }
    current.setDate(current.getDate() + 1);
  }

  return businessDays;
}

/**
 * Check if a date is a weekend
 * @param {Date|string} date - Date to check
 * @returns {boolean} True if weekend, false otherwise
 */
export function isWeekend(date) {
  const d = new Date(date);
  const dayOfWeek = d.getDay();
  return dayOfWeek === 0 || dayOfWeek === 6;
}

/**
 * Get the next business day after a given date
 * @param {Date|string} date - Starting date
 * @returns {Date} Next business day
 */
export function getNextBusinessDay(date) {
  const next = new Date(date);
  next.setDate(next.getDate() + 1);
  while (isWeekend(next)) {
    next.setDate(next.getDate() + 1);
  }
  return next;
}

/**
 * Get the previous business day before a given date
 * @param {Date|string} date - Starting date
 * @returns {Date} Previous business day
 */
export function getPreviousBusinessDay(date) {
  const prev = new Date(date);
  prev.setDate(prev.getDate() - 1);
  while (isWeekend(prev)) {
    prev.setDate(prev.getDate() - 1);
  }
  return prev;
}

/**
 * Calculate leave days excluding weekends
 * For half-day leaves, returns 0.5
 * @param {Date|string} startDate - Leave start date
 * @param {Date|string} endDate - Leave end date
 * @param {boolean} isHalfDay - Whether it's a half-day leave
 * @returns {number} Number of leave days (excluding weekends)
 */
export function calculateLeaveDays(startDate, endDate, isHalfDay = false) {
  if (isHalfDay) {
    return 0.5;
  }

  return calculateBusinessDays(startDate, endDate, true);
}

/**
 * Format duration in days
 * @param {number} days - Number of days
 * @returns {string} Formatted duration
 */
export function formatDuration(days) {
  if (!days) return "0 days";
  if (days === 0.5) return "0.5 days (Half day)";
  if (days === 1) return "1 day";
  return `${days} days`;
}
